<script setup>
import { computed, onMounted, ref } from 'vue';
import SearchBar from './components/SearchBar.vue';
import DealerList from './components/DealerList.vue';
import DealerDetails from './components/DealerDetails.vue';
import MapPanel from './components/MapPanel.vue';
import EmptyState from './components/EmptyState.vue';
import FilterPopup from './components/FilterPopup.vue';
import { useGeoCascade } from './composables/useGeoCascade';
import { useGoogleMaps } from './composables/useGoogleMaps';
import { useDealers } from './composables/useDealers';
import { distanceMi, isWithinBounds } from './utils/distance';
import { isMapsDisabled } from './utils/debugMode';
import { t } from './i18n';

const props = defineProps({
	apiKey: { type: String, required: true },
	restUrl: { type: String, required: true },
	restrictByAreaOnLoad: { type: Boolean, default: false },  //true - all related in radius /  false - all 
	nearestLimit: { type: Number, default: 50 }, //max dealers to show
	radiusMi: { type: Number, default: 230 },
	mapSyncMode: { type: String, default: 'dynamic' }, //trigger / dynamic
});

// state
const STATE = { LOADING: 'loading', RESULTS: 'results', EMPTY: 'empty' };
const viewState = ref(STATE.LOADING);
const isLocating = ref(true);
const searchQuery = ref('');
const mapElRef = ref(null);

const selectedDealer = ref(null);

const activeRadiusMi = ref(props.radiusMi);
const isRadiusFilterActive = ref(props.restrictByAreaOnLoad);
const isFiltersOpen = ref(false);

const MAP_SYNC = { DYNAMIC: 'dynamic', TRIGGER: 'trigger' };
const mapSyncMode = computed(() => (props.mapSyncMode === MAP_SYNC.DYNAMIC ? MAP_SYNC.DYNAMIC : MAP_SYNC.TRIGGER));
const showReloadButton = ref(false);
let pendingMapBounds = null;
let pendingMapCenter = null;

const { resolveInitialLocation } = useGeoCascade();
const { dealers, isLoading: isDealersLoading, fetchDealers } = useDealers(props.restUrl);
const {
	initMap,
	setDealerMarkers,
	setActiveDealer,
	clearActiveDealer,
	fitBounds,
	panTo,
	saveMapView,
	restoreMapView,
	onUserBoundsChange,
	waitForIdle,
	attachAutocomplete,
	geocodeQuery,
	google,
} = useGoogleMaps(props.apiKey);

const mapsReady = computed(() => !!google.value);

let searchOrigin = null;
let userLocation = null;
let initialLocation = null; // { lat, lng, zoom } from geolocation cascade — used by "reset" 
let currentMode = 'browse'; // browse / search / area
let lastViewport = null;

const visibleDealers = ref([]);

function withDistance(list, origin) {
	return list
		.map((d) => ({ ...d, distance: origin ? distanceMi(origin, d) : undefined }))
		.sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
}

function applyRadiusIfActive(list, origin) {
	if (!isRadiusFilterActive.value || !origin) return list;
	return list.filter((d) => d.distance <= activeRadiusMi.value);
}
function applyNearbyRadius(list) {
	return list.filter((d) => d.distance <= props.radiusMi);
}

function isBroadAreaResult(place) {
	return !!place.viewport && (place.types || []).some((t) => t === 'country' || t === 'administrative_area_level_1');
}

// example 
// renderDealers({ mode: 'browse', origin, limit: props.nearestLimit });
// renderDealers({ mode: 'search', origin });
// renderDealers({ mode: 'area', origin, viewport });
// renderDealers({ mode: 'area', origin, viewport, movesMap: false }); - refresh list/markers only, keep map as-is
function renderDealers({ mode, origin, viewport = null, limit = null, movesMap = true }) {
	currentMode = mode;
	if (mode === 'area') lastViewport = viewport;

	let list = mode === 'area' ? dealers.value.filter((d) => isWithinBounds(d, viewport)) : dealers.value;

	list = withDistance(list, origin);

	if (mode === 'browse' && !isRadiusFilterActive.value && origin) list = applyNearbyRadius(list);

	list = applyRadiusIfActive(list, origin);
	if (limit) list = list.slice(0, limit);

	visibleDealers.value = list;
	viewState.value = list.length ? STATE.RESULTS : STATE.EMPTY;

	if (movesMap) {
		showReloadButton.value = false;
		pendingMapBounds = null;
		syncMap(list, { fit: mode === 'area', fallbackCenter: origin });
	} else if (mapsReady.value) {
		setDealerMarkers(list, { onSelect: (dealer) => openDetails(dealer) });
	}
}

function refreshCurrentView() {
	if (currentMode === 'area' && lastViewport) {
		renderDealers({ mode: 'area', origin: searchOrigin, viewport: lastViewport });
	} else if (currentMode === 'search' && searchOrigin) {
		renderDealers({ mode: 'search', origin: searchOrigin });
	} else {
		renderDealers({ mode: 'browse', origin: searchOrigin, limit: props.nearestLimit });
	}
}
function syncMap(list, { fit = false, fallbackCenter = null } = {}) {
	if (!mapsReady.value) return;

	setDealerMarkers(list, { onSelect: (dealer) => openDetails(dealer) });

	if (fit && list.length) {
		fitBounds(list);
	} else if (fallbackCenter) {
		panTo(fallbackCenter.lat, fallbackCenter.lng, 9);
	}
}

// dealer activation
function openDetails(dealer) {
	selectedDealer.value = dealer;
	setActiveDealer(dealer.id);
	saveMapView();
	panTo(dealer.lat, dealer.lng, 15);
	showReloadButton.value = false;
	pendingMapBounds = null;
}

function closeDetails() {
	selectedDealer.value = null;
	clearActiveDealer();
	restoreMapView();
}


// search: Places Autocomplete 
let searchToken = 0;

async function runSearch(query) {
	const token = ++searchToken;
	searchQuery.value = query;

	if (!query) {
		searchOrigin = userLocation;
		renderDealers({ mode: 'browse', origin: searchOrigin, limit: props.nearestLimit });
		return;
	}

	if (isMapsDisabled()) {
		currentMode = 'search';
		const q = query.toLowerCase();
		const matched = dealers.value.filter((d) =>
			[d.name, d.city, d.state, d.zip, d.address].some((field) =>
				field?.toLowerCase().includes(q)
			)
		);
		visibleDealers.value = withDistance(matched, searchOrigin);
		viewState.value = matched.length ? STATE.RESULTS : STATE.EMPTY;
		return;
	}

	viewState.value = STATE.LOADING;
	const place = await geocodeQuery(query);
	if (token !== searchToken) return;

	if (!place) {
		visibleDealers.value = [];
		viewState.value = STATE.EMPTY;
		return;
	}

	searchOrigin = { lat: place.lat, lng: place.lng };

	if (isBroadAreaResult(place)) {
		renderDealers({ mode: 'area', origin: searchOrigin, viewport: place.viewport });
	} else {
		renderDealers({ mode: 'search', origin: searchOrigin });
	}
}

function onPlaceSelected(place) {
	searchQuery.value = place.label;
	searchOrigin = { lat: place.lat, lng: place.lng };

	if (isBroadAreaResult(place)) {
		renderDealers({ mode: 'area', origin: searchOrigin, viewport: place.viewport });

	} else {
		renderDealers({ mode: 'search', origin: searchOrigin });
	}
}

// popup actions
function onApplyRadius({ radiusMi }) {
	isRadiusFilterActive.value = true;
	activeRadiusMi.value = radiusMi;
	refreshCurrentView();
}

function onResetFilters() {
	isRadiusFilterActive.value = false;
	activeRadiusMi.value = props.radiusMi;
	resetToMyLocation();
}

function onMapBoundsChanged(bounds, center) {
	if (selectedDealer.value) return;

	if (mapSyncMode.value === MAP_SYNC.DYNAMIC) {
		applyMapBounds(bounds, center);
	} else {
		pendingMapBounds = bounds;
		pendingMapCenter = center;
		showReloadButton.value = true;
	}
}

function applyMapBounds(viewport, center) {
	if (center) searchOrigin = center;

	renderDealers({ mode: 'area', origin: searchOrigin, viewport, movesMap: false });
	showReloadButton.value = false;
	pendingMapBounds = null;
	pendingMapCenter = null;
}

function onReloadList() {
	if (pendingMapBounds) applyMapBounds(pendingMapBounds, pendingMapCenter);
}

function resetToMyLocation() {
	if (!initialLocation) return;

	searchOrigin = userLocation;
	searchQuery.value = '';
	showReloadButton.value = false;
	pendingMapBounds = null;
	pendingMapCenter = null;

	panTo(initialLocation.lat, initialLocation.lng, initialLocation.zoom);
	renderDealers({ mode: 'browse', origin: searchOrigin, limit: props.nearestLimit, movesMap: false });
}

async function bootstrap(mapContainer) {
	const [location] = await Promise.all([resolveInitialLocation(), fetchDealers()]);

	userLocation = { lat: location.lat, lng: location.lng };
	searchOrigin = { lat: location.lat, lng: location.lng };
	initialLocation = { lat: location.lat, lng: location.lng, zoom: location.zoom };
	isLocating.value = false;

	await initMap(mapContainer, location);
	renderDealers({ mode: 'browse', origin: searchOrigin, limit: props.nearestLimit });

	if (mapsReady.value) {
		await waitForIdle();
		onUserBoundsChange(onMapBoundsChanged);
	}
}

function onMapReady(mapContainer) {
	mapElRef.value = mapContainer;
	bootstrap(mapContainer);
}
</script>

<template>
	<div class="dealer-locator__body" :class="`dealer-locator__body--${viewState}`">
		<div class="dealer-locator__sidebar">
			<SearchBar v-model="searchQuery" :maps-ready="mapsReady" :attach-autocomplete="attachAutocomplete"
				@search="runSearch" @place-selected="onPlaceSelected" @open-filters="isFiltersOpen = true" />

			<button v-if="selectedDealer" type="button" class="dealer-locator__back" @click="closeDetails">
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
					<path fill="#fff" d="M7.8 11H20v2H7.8l5.4 5.4-1.4 1.4L4 12l7.8-7.8 1.4 1.4z" />
				</svg>
				{{ t.backToList }}
			</button>
			<template v-else>
				<p class="dealer-locator__count">
					<template v-if="isDealersLoading">
						{{ t.loadingDealers }}
					</template>
					<template v-else>
						<span>{{ t.numberOfShops }}</span>
						<span>{{ visibleDealers.length }}</span>
					</template>
				</p>
			</template>
			<Transition name="dealer-view" mode="out-in">

				<DealerDetails v-if="selectedDealer" :dealer="selectedDealer" />

				<template v-else>
					<template v-if="viewState !== STATE.EMPTY">
						<DealerList :dealers="visibleDealers" :is-loading="isDealersLoading || viewState === STATE.LOADING"
							@view-details="openDetails" />
					</template>

					<EmptyState v-else :query="searchQuery" />
				</template>
			</Transition>
		</div>

		<MapPanel :is-locating="isLocating" :show-reload-button="showReloadButton" @ready="onMapReady"
			@reload-list="onReloadList" />
	</div>
	<FilterPopup v-model="isFiltersOpen" :radius-mi="activeRadiusMi" :radius-min="5" :radius-max="500"
		@apply="onApplyRadius" @reset="onResetFilters" />
</template>
